import { useFormik } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import { FaEuroSign, FaPercent } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Checkbox, Input, Switch, Text } from 'rizzui';
import CustomSelect from '../../../components/forms/CustomSelect';
import CustomSelectApiHookForm from '../../../components/forms/CustomSelectApiHookForms';
import RequiredInput from '../../../components/forms/InputHOC';
import usePaymentMethods from '../../../hooks/api-calls/contract/usePaymentMethods';
import useInvoiceClients from '../../../hooks/api-calls/invoices/useInvoiceClients';
import useInvoiceCorporations from '../../../hooks/api-calls/invoices/useInvoiceCorporations';
import Page from '../../../layout/Page/Page';
import FormGroup from '../../../layout/shared/form-group';
import FormFooter from '../../_layout/_footers/form-footer';
import InvoiceLinesForm from '../components/InvoiceLinesForm';
import { calculateInvoiceTotals } from '../utils/calculateInvoiceTotals';
import { externalClientNifValidation, externalClientPostalCodeValidation } from '../utils/externalClientValidation';
import {
    IVA_NON_SUBJECT_LEGAL_TEXT,
    shouldApplyNonSubjectIvaForNonStandard,
} from '../utils/ivaTerritory';

const RECEIVER_TYPE_OPTIONS = [
    { value: 'system_client', label: 'Cliente del sistema' },
    { value: 'fiscal_company', label: 'Empresa fiscal del sistema' },
    { value: 'external_client', label: 'Cliente externo' },
];

const lineValidation = yup.object().shape({
    concept: yup.string().required('Campo obligatorio'),
    amount: yup
        .number()
        .required('Campo obligatorio')
        .min(0, 'El valor debe ser mayor o igual a 0')
        .test('maxDecimals', 'Máximo 2 decimales', (v) => !v || /^\d+(\.\d{1,2})?$/.test(v.toString())),
    tax: yup
        .number()
        .required('Campo obligatorio')
        .min(0)
        .max(100),
    units: yup
        .number()
        .required('Campo obligatorio')
        .min(0)
        .test('isInteger', 'Debe ser entero', (v) => !v || Number.isInteger(v)),
    totalWithoutTax: yup.number().required('Campo obligatorio').min(0),
    totalWithTax: yup.number().required('Campo obligatorio').min(0),
});

const schema = yup.object({
    corporationId: yup.string().required('Campo obligatorio'),
    paymentMethodId: yup.string().required('Campo obligatorio'),
    invoiceDate: yup.date().required('Campo obligatorio'),
    expirationDate: yup.date().required('Campo obligatorio'),
    receiverType: yup.string().oneOf(['system_client', 'fiscal_company', 'external_client']).required('Campo obligatorio'),
    clientId: yup.string().nullable().when('receiverType', {
        is: 'system_client',
        then: (s) => s.required('Selecciona un cliente del sistema'),
        otherwise: (s) => s.nullable().notRequired(),
    }),
    providingCorporationId: yup.string().nullable().when('receiverType', {
        is: 'fiscal_company',
        then: (s) => s.required('Selecciona una empresa fiscal receptora'),
        otherwise: (s) => s.nullable().notRequired(),
    }),
    externalClientName: yup.string().nullable().when('receiverType', {
        is: 'external_client',
        then: (s) => s.required('El nombre o razón social es obligatorio'),
        otherwise: (s) => s.nullable().notRequired(),
    }),
    externalClientNif: externalClientNifValidation,
    externalClientPostalCode: externalClientPostalCodeValidation,
    tax: yup.number().min(0).max(100).required('Campo obligatorio'),
    irpf: yup.number().min(0).max(100).nullable().notRequired(),
    irc: yup.number().min(0).max(100).nullable().notRequired(),
    lines: yup.array().of(lineValidation).min(1, 'Añade al menos una línea de factura'),
});

interface NonStandardInvoiceFormProps {
    isLoading: boolean;
    submit: Function;
}

const NonStandardInvoiceForm: React.FC<NonStandardInvoiceFormProps> = ({ isLoading, submit }) => {
    const navigate = useNavigate();
    const today = new Date().toISOString().split('T')[0];

    const [allCorporationsData, setAllCorporationsData] = useState<any[]>([]);

    const { getInvoiceCorporationsList } = useInvoiceCorporations({ active: true });
    const { fetchInvoiceClients } = useInvoiceClients({ active: true });
    const { getPaymentMethodsList } = usePaymentMethods();

    useEffect(() => {
        const list = getInvoiceCorporationsList();
        if (list?.length) setAllCorporationsData(list);
    }, [getInvoiceCorporationsList]);

    const formik = useFormik({
        initialValues: {
            invoiceType: 'non_standard_invoice',
            corporationId: '',
            paymentMethodId: '',
            invoiceDate: today,
            expirationDate: today,
            paymentDate: null as string | null,
            receiverType: '' as 'system_client' | 'fiscal_company' | 'external_client' | '',
            clientId: null as string | null,
            clientName: '',
            clientProvince: '',
            clientPostalCode: '',
            providingCorporationId: null as string | null,
            externalClientName: '',
            externalClientNif: '',
            externalClientAddress: '',
            externalClientProvince: '',
            externalClientLocality: '',
            externalClientPostalCode: '',
            externalClientContact: '',
            tax: 21,
            irpf: null as number | null,
            irc: null as number | null,
            ivaExempt: false,
            taxableAmount: 0,
            totalTaxesAmount: 0,
            totalPrice: 0,
            totalProducts: null as number | null,
            lines: [] as any[],
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: (values) => { submit(values); },
    });

    useEffect(() => {
        const totals = calculateInvoiceTotals(formik.values.lines, {
            tax: formik.values.tax || 0,
            irpf: formik.values.irpf,
            irc: formik.values.irc,
            ivaExempt: formik.values.ivaExempt,
        });

        if (formik.values.taxableAmount !== totals.taxableAmount) {
            formik.setFieldValue('taxableAmount', totals.taxableAmount);
        }
        if (formik.values.totalTaxesAmount !== totals.totalTaxesAmount) {
            formik.setFieldValue('totalTaxesAmount', totals.totalTaxesAmount);
        }
        if (formik.values.totalPrice !== totals.totalPrice) {
            formik.setFieldValue('totalPrice', totals.totalPrice);
        }
    }, [
        formik.values.lines,
        formik.values.tax,
        formik.values.irpf,
        formik.values.irc,
        formik.values.ivaExempt,
        formik.values.taxableAmount,
        formik.values.totalTaxesAmount,
        formik.values.totalPrice,
    ]);

    useEffect(() => {
        const province = formik.values.receiverType === 'external_client'
            ? formik.values.externalClientProvince
            : formik.values.clientProvince;
        const postalCode = formik.values.receiverType === 'external_client'
            ? formik.values.externalClientPostalCode
            : formik.values.clientPostalCode;

        const shouldExempt = shouldApplyNonSubjectIvaForNonStandard(
            formik.values.receiverType,
            province,
            postalCode
        );

        if (shouldExempt !== formik.values.ivaExempt) {
            formik.setFieldValue('ivaExempt', shouldExempt);
        }

        if (shouldExempt && formik.values.tax !== 0) {
            formik.setFieldValue('tax', 0);
        } else if (!shouldExempt && formik.values.receiverType !== 'fiscal_company' && formik.values.tax === 0) {
            formik.setFieldValue('tax', 21);
        }
    }, [
        formik.values.receiverType,
        formik.values.externalClientProvince,
        formik.values.externalClientPostalCode,
        formik.values.clientProvince,
        formik.values.clientPostalCode,
    ]);

    const handleReceiverTypeChange = (value: string) => {
        formik.setFieldValue('receiverType', value);
        formik.setFieldValue('clientId', null);
        formik.setFieldValue('clientName', '');
        formik.setFieldValue('clientProvince', '');
        formik.setFieldValue('clientPostalCode', '');
        formik.setFieldValue('providingCorporationId', null);
        formik.setFieldValue('externalClientName', '');
        formik.setFieldValue('externalClientNif', '');
        formik.setFieldValue('externalClientAddress', '');
        formik.setFieldValue('externalClientProvince', '');
        formik.setFieldValue('externalClientLocality', '');
        formik.setFieldValue('externalClientPostalCode', '');
        formik.setFieldValue('externalClientContact', '');
    };

    return (
        <Fragment>
            <Page container="fluid">
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        {/* Emisor */}
                        <FormGroup
                            title="Empresa emisora"
                            description="Empresa fiscal que emite la factura"
                            className="pt-3 pb-4"
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-2"
                        >
                            <CustomSelect
                                isSearchable
                                isClearable
                                id="corporationId"
                                label="Empresa fiscal emisora"
                                required
                                value={{ value: formik.values.corporationId, label: allCorporationsData.find((g: any) => g.value === formik.values.corporationId)?.label }}
                                options={allCorporationsData}
                                onChange={(e: any) => formik.setFieldValue('corporationId', e?.value ?? '')}
                                error={formik.errors.corporationId as string}
                            />

                            <RequiredInput
                                id="invoiceDate"
                                type="date"
                                label="Fecha de factura"
                                formik={formik}
                            />

                            <CustomSelect
                                isSearchable
                                isClearable
                                id="paymentMethodId"
                                label="Método de pago"
                                required
                                value={formik.values.paymentMethodId ? { value: formik.values.paymentMethodId, label: getPaymentMethodsList().find((item: any) => item.value === formik.values.paymentMethodId)?.label } : null}
                                options={getPaymentMethodsList()}
                                onChange={(e: any) => formik.setFieldValue('paymentMethodId', e?.value ?? '')}
                                error={formik.errors.paymentMethodId as string}
                            />

                            <RequiredInput
                                id="expirationDate"
                                type="date"
                                label="Fecha de vencimiento"
                                formik={formik}
                            />

                            <Input
                                id="paymentDate"
                                type="date"
                                label="Fecha de pago"
                                value={formik.values.paymentDate ?? ''}
                                onChange={(e: any) => formik.setFieldValue('paymentDate', e.target.value)}
                            />
                        </FormGroup>

                        {/* Receptor */}
                        <FormGroup
                            title="Receptor"
                            description="Datos del receptor de la factura"
                            className="pt-3 pb-4"
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-2"
                        >
                            <div className="col-span-full">
                                <CustomSelect
                                    isSearchable={false}
                                    isClearable={false}
                                    id="receiverType"
                                    label="Tipo de receptor"
                                    required
                                    value={RECEIVER_TYPE_OPTIONS.find(o => o.value === formik.values.receiverType) ?? null}
                                    options={RECEIVER_TYPE_OPTIONS}
                                    onChange={(e: any) => handleReceiverTypeChange(e?.value ?? '')}
                                    error={formik.errors.receiverType as string}
                                />
                            </div>

                            {formik.values.receiverType === 'system_client' && (
                                <div className="col-span-full">
                                    <CustomSelectApiHookForm
                                        id="clientId"
                                        label="Cliente"
                                        formik={formik}
                                        fetchOptions={async (searchText: string | null) => {
                                            const text = searchText || formik.values.clientName;
                                            return fetchInvoiceClients({ search_text: text });
                                        }}
                                        setValue={(data: any) => {
                                            formik.setFieldValue(
                                                'clientProvince',
                                                data?.object?.registeredOfficeProvince ?? ''
                                            );
                                            formik.setFieldValue(
                                                'clientPostalCode',
                                                data?.object?.registeredOfficePostalCode ?? ''
                                            );
                                        }}
                                        error={formik.errors.clientId as string}
                                        minInputLength={3}
                                    />
                                </div>
                            )}

                            {formik.values.receiverType === 'fiscal_company' && (
                                <div className="col-span-full">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id="providingCorporationId"
                                        label="Empresa fiscal receptora"
                                        required
                                        value={{ value: formik.values.providingCorporationId, label: allCorporationsData.find((g: any) => g.value === formik.values.providingCorporationId)?.label }}
                                        options={allCorporationsData.filter((c: any) => c.value !== formik.values.corporationId)}
                                        onChange={(e: any) => formik.setFieldValue('providingCorporationId', e?.value ?? null)}
                                        error={formik.errors.providingCorporationId as string}
                                    />
                                </div>
                            )}

                            {formik.values.receiverType === 'external_client' && (
                                <>
                                    <RequiredInput
                                        id="externalClientName"
                                        type="text"
                                        label="Nombre o razón social"
                                        formik={formik}
                                    />
                                    <RequiredInput
                                        id="externalClientNif"
                                        type="text"
                                        label="NIF / CIF"
                                        formik={formik}
                                    />
                                    <RequiredInput
                                        id="externalClientAddress"
                                        type="text"
                                        label="Dirección"
                                        formik={formik}
                                    />
                                    <RequiredInput
                                        id="externalClientProvince"
                                        type="text"
                                        label="Provincia"
                                        formik={formik}
                                    />
                                    <RequiredInput
                                        id="externalClientLocality"
                                        type="text"
                                        label="Localidad"
                                        formik={formik}
                                    />
                                    <RequiredInput
                                        id="externalClientPostalCode"
                                        type="text"
                                        label="Código postal"
                                        formik={formik}
                                    />
                                    <div className="col-span-full">
                                        <RequiredInput
                                            id="externalClientContact"
                                            type="text"
                                            label="Datos de contacto"
                                            formik={formik}
                                        />
                                    </div>
                                </>
                            )}
                        </FormGroup>

                        {/* Líneas de factura */}
                        <InvoiceLinesForm formik={formik} manualLinesOnly />

                        {/* Fiscalidad */}
                        <FormGroup
                            title="Fiscalidad"
                            description="Impuestos y retenciones aplicables"
                            className="pt-3 pb-4"
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-3"
                        >
                            <RequiredInput
                                id="tax"
                                type="number"
                                label="IVA (%)"
                                suffix={<FaPercent />}
                                formik={formik}
                                disabled={formik.values.ivaExempt}
                            />

                            <RequiredInput
                                id="irpf"
                                type="number"
                                label="IRPF (%)"
                                suffix={<FaPercent />}
                                formik={formik}
                            />

                            <RequiredInput
                                id="irc"
                                type="number"
                                label="IRC (%)"
                                suffix={<FaPercent />}
                                formik={formik}
                            />

                            <div className="col-span-full flex flex-col gap-2">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="ivaExempt"
                                        label="IVA no sujeto (Canarias, Ceuta y Melilla)"
                                        checked={formik.values.ivaExempt}
                                        onChange={(e) => formik.setFieldValue('ivaExempt', e.target.checked)}
                                        disabled
                                    />
                                </div>
                                {formik.values.ivaExempt && (
                                    <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                        <span className="font-medium">Texto legal obligatorio: </span>
                                        {IVA_NON_SUBJECT_LEGAL_TEXT}
                                    </div>
                                )}
                            </div>
                        </FormGroup>

                        {/* Totales */}
                        <FormGroup
                            title="Totales"
                            description="Importes calculados automáticamente"
                            className="pt-3 pb-4"
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-3"
                        >
                            <RequiredInput
                                id="taxableAmount"
                                type="number"
                                label="Base imponible"
                                suffix={<FaEuroSign />}
                                formik={formik}
                                disabled
                            />

                            <RequiredInput
                                id="totalTaxesAmount"
                                type="number"
                                label="Total IVA"
                                suffix={<FaEuroSign />}
                                formik={formik}
                                disabled
                            />

                            <RequiredInput
                                id="totalPrice"
                                type="number"
                                label="Total factura"
                                suffix={<FaEuroSign />}
                                formik={formik}
                                disabled
                            />
                        </FormGroup>
                    </form>
                </div>
            </Page>

            <FormFooter
                submitBtnText="Crear Factura Atípica"
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </Fragment>
    );
};

export default NonStandardInvoiceForm;
